import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const tableCellStyle = {
  border: "1px solid #ddd",
  textAlign: "center",
  padding: "4px",
  fontSize: "0.8rem",
};

const BeautyDashboard = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const location = useLocation();
  const pathname = location.pathname;

  // Local state for filters
  const [keyword, setKeyword] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  // This state is used only when we are not in a route that forces a record type.
  const [userRecordType, setUserRecordType] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  // Fetch combined data from the API with filters included in the URL.
  const {
    data: combinedData,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["combine-procedure", page, rowsPerPage, keyword, filterStatus],
    `beauty-dashboard/?search=${keyword}&page=${page}&page_size=${rowsPerPage}&status=${filterStatus}`,
    token
  );

  // Refetch when any filter or pagination changes.
  useEffect(() => {
    refetch();
  }, [keyword, filterStatus, page, rowsPerPage, refetch]);

  useEffect(() => {
    if (combinedData && combinedData.count !== undefined) {
      setTotalRecords(combinedData.count);
    }
  }, [combinedData]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Create a sorted copy of the results:
  // Records with procedure_date equal to today are moved to the end.
  const sortedRecords = combinedData?.results
    ? [...combinedData.results].sort((a, b) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        dateA.setHours(0, 0, 0, 0);
        dateB.setHours(0, 0, 0, 0);
        const aIsToday = dateA.getTime() === today.getTime();
        const bIsToday = dateB.getTime() === today.getTime();
        if (aIsToday && !bIsToday) return 1; // a goes after b
        if (!aIsToday && bIsToday) return -1; // a goes before b
        // Otherwise sort descending by the actual date (latest first)
        return new Date(b.date) - new Date(a.date);
      })
    : [];

  // Helper function for row style based on procedure date.
  // Today: light green background with white text.
  // Tomorrow: light blue background with white text.
  const getRowStyle = (record) => {
    if (!record.date) return {};
    const recordDate = new Date(record.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    recordDate.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    // Check if the record date is today or tomorrow and apply corresponding styles
    if (recordDate.getTime() === today.getTime()) {
      return { backgroundColor: "#9ef0a5", color: "white" }; // Green color for today
    } else if (recordDate.getTime() === tomorrow.getTime()) {
      return { backgroundColor: "#b1b4fa", color: "white" }; // Blue color for tomorrow
    }

    // Default style for other records
    return { backgroundColor: "white", color: "black" };
  };

  return (
    <Grid container justifyContent="center">
      <Paper sx={{ p: 2, width: "100%", borderRadius: 2, boxShadow: 3 }}>
        {/* Page Title */}
        <Typography
          variant="h5"
          fontWeight="bold"
          align="center"
          sx={{ pb: 2, borderBottom: "5px solid #007471" }}
        >
          {t("call_center.dashboard", "BeautyDashboard")}
        </Typography>

        {/* Redesigned Filter Panel */}
        <Paper
          sx={{
            p: 2,
            mt: 1,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#f1f1f1",
            boxShadow: 1,
          }}
          variant="outlined"
        >
          <Grid container spacing={3} alignItems="center">
            {/* Search Field */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="medium">
                <TextField
                  label={t("call_center.search")}
                  variant="outlined"
                  size="medium"
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: "100%", sm: "300px" } }}
                />
              </FormControl>
            </Grid>

            {/* Record Type Filter - only show if not forced by the route */}
            {pathname !== "/htPatient" && pathname !== "/beauty" && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="medium">
                  <InputLabel id="record-type-label">
                    {t("call_center.recordType", "Record Type")}
                  </InputLabel>
                  <Select
                    labelId="record-type-label"
                    label={t("call_center.recordType", "Record Type")}
                    value={userRecordType}
                    onChange={(e) => {
                      setUserRecordType(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="">{t("call_center.all")}</MenuItem>
                    <MenuItem value="ht">HT</MenuItem>
                    <MenuItem value="procedure">Procedure</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Table Section */}
        <TableContainer sx={{ mt: 3 }}>
          <Table stickyHeader size="small">
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={tableCellStyle}>{t("call_center.id")}</TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.patient")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.contact")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.address", "Address")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.procedureType")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.date")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.procedureDay")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.period")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.zone")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.totalCost")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.advance")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.status")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.recordType")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRecords.map((record, i) => (
                // <TableRow key={record.id} hover style={getRowStyle(record)}>
                <TableRow key={record.id} hover>
                  <TableCell sx={tableCellStyle}>{i + 1}</TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {record.patient}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {record.phone}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {record?.patient_address}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {record.procedure_type}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {record.date ? FormatDateTime(record.date) : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellStyle,
                      fontWeight: "bold",
                      textTransform: "capitalize",
                    }}
                  >
                    {record.procedure_day}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {record.period}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {record.zone}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {record.total_cost || ""}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {record.total_paid || ""}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    <Chip
                      sx={{ fontWeight: "bold", color: "blue" }}
                      label={record.status_name}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ ...tableCellStyle, textTransform: "capitalize" }}
                  >
                    {record.record_type}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <Box
          mt={2}
          mb={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{t("call_center.rowsPerPage")}:</Typography>
            <FormControl size="small">
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
                <MenuItem value={1000}>1000</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={Math.ceil(totalRecords / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
          />
        </Box>
      </Paper>
    </Grid>
  );
};

export default BeautyDashboard;
